import React, { useState, useRef, useEffect } from "react";
import backIcon from "../../Assets/Images/Expand_left.png";
import cameraIcon from "../../Assets/Images/CameraIcon.png";
import galleryIcon from "../../Assets/Images/gallery.png";
import documentIcon from "../../Assets/Images/document.png";
import checkIcon from '../../Assets/Images/CheckIcon.png';
import "./LabReports.css";
import { useNavigate } from "react-router-dom";

const LabReports = () => {
  const [files, setFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);
  const [showCamera, setShowCamera] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleGalleryUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const newFiles = uploadedFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      uploadDate: new Date().toISOString(),
    }));
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    setShowModal(false);
  };

  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 6);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing the camera: ", error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
    setPreviewImage(null);
  };

  const handleCaptureClick = () => {
    const context = canvasRef.current.getContext("2d");
    context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
    const imageDataURL = canvasRef.current.toDataURL("image/png");
    setPreviewImage(imageDataURL);
  };

  const handleConfirmClick = () => {
    if (previewImage) {
      setFiles((prevFiles) => [
        ...prevFiles,
        {
          name: `Captured Image ${files.length + 1}`,
          url: previewImage,
          uploadDate: new Date().toISOString(),
        },
      ]);

      // Simulate upload success or failure
      const isUploadSuccessful = uploadImage(previewImage); // Simulate the image upload logic

      // Navigate to success or failure page based on upload result
      if (isUploadSuccessful) {
        navigate("/mymedicineupload-success"); // Redirect to success page
      } else {
        navigate("/mymedicineupload-failure"); // Redirect to failure page
      }

      stopCamera(); // Stop the camera after confirming the image
    }
  };

  const uploadImage = (image) => {
    // Simulated upload function (replace with actual upload logic)
    return true; // Simulate success for now
  };

  const handleTakePhotoClick = () => {
    setShowModal(false);
    setShowCamera(true);
    startCamera();
  };

  const handleCloseCamera = () => {
    stopCamera();
  };

  useEffect(() => {
    return () => {
      stopCamera(); // Clean up camera when component unmounts
    };
  }, []);

  return (
    <div className="medicalrecordupload-container">
      <div className="upload-header">
        <button className="back-button" onClick={handleBackClick}>
          <img src={backIcon} alt="Back" className="back-icon" />
        </button>
        <h1>Lab Reports</h1>
      </div>
  
      {/* Render image grid */}
      {!showCamera && (
        <div className="image-grid">
          {files
            .slice()
            .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
            .slice(0, visibleCount)
            .map((file, index) => (
              <div key={index} className="image-wrapper">
                <div className="image-container">
                  <div className="image-meta">
                    <p className="upload-date">Self Upload</p>
                    <p className="upload-date">
                      {new Date(file.uploadDate).toLocaleDateString()}
                    </p>
                  </div>
                  <img
                    src={file.url}
                    alt={`Uploaded ${index}`}
                    className="uploaded-image"
                  />
                </div>
              </div>
            ))}
        </div>
      )}
  
      {/* Show "Load More" only if not in camera view */}
      {!showCamera && files.length > visibleCount && (
        <div className="load-more" onClick={handleLoadMore}>
          <p>Load More</p>
        </div>
      )}
  
      {/* Hide upload button when camera is active */}
      {!showCamera && (
        <div className="upload-button-container">
          <button className="upload-button" onClick={toggleModal}>
            Upload
          </button>
        </div>
      )}
  
      {/* Modal upload options */}
      {showModal && !showCamera && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              toggleModal();
            }
          }}
        >
          <div className="modal">
            <div className="divider"></div>
            <h3>Upload Your Report</h3>
            <button className="modal-option" onClick={handleTakePhotoClick}>
              <img src={cameraIcon} alt="Camera Icon" className="labicon" />
              Take A Photo
            </button>
            <label className="modal-option">
              <img src={galleryIcon} alt="Gallery Icon" className="labicon" />
              <div className="modal-text">
                Upload From Gallery{" "}
                <span className="file-size">Max size: 2 MB</span>
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryUpload}
                style={{ display: "none" }}
              />
            </label>
            <label className="modal-option">
              <img src={documentIcon} alt="Document Icon" className="labicon" />
              <div className="modal-text">
                Upload File <span className="file-size">Max size: 2 MB</span>
              </div>
              <input
                type="file"
                multiple
                onChange={handleGalleryUpload}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>
      )}
  
      {/* Camera view */}
      {showCamera && (
        <div className="camera-container">
          <div className="camera-header">
            <button className="back-button" onClick={handleCloseCamera}>
              <img src={backIcon} alt="Back" className="icon-image" />
            </button>
            <span>Camera</span>
          </div>
          <div className="camera-view">
            {previewImage ? (
              <img src={previewImage} alt="Captured" style={{ width: "100%" }} />
            ) : (
              <video ref={videoRef} autoPlay style={{ width: "100%" }} />
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} width="640" height="480" />
          </div>
          <div className="camera-footer">
            <div className="camera-capture-button" onClick={handleCaptureClick}>
              <span></span> {/* Placeholder for the capture button */}
            </div>
            <img
              src={checkIcon}
              onClick={handleConfirmClick}
              alt="Check"
              className="footer-icon"
              style={{
                opacity: previewImage ? 1 : 0.5, // Enable if preview is available
                pointerEvents: previewImage ? "auto" : "none", // Prevent clicking when disabled
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LabReports;
